// Task #6095 — account-level consent sync for storage-blocked browsers.
//
// iPhone Safari with "Block All Cookies" (or Lockdown Mode) blocks BOTH
// cookies and localStorage, so the browser-side three-layer consent store
// can't survive a reload and the privacy banner re-shows on every page load.
// For logged-in users the account is the only durable store the device can't
// block: AuthProvider calls `connectAccountConsent(token)` once a session +
// profile exist, which
//   1. registers the account write-through (setConsent → PUT /users/me/consent),
//   2. runs a one-shot two-way sync: the NEWER `ts` wins between the
//      browser-stored copy and the account-stored copy. A newer local choice
//      is pushed up; a newer (or only) account choice is hydrated down via
//      the consent lib so the banner closes/never opens and consent-gated
//      SDK loading reacts through the existing listener mechanism.
import * as Sentry from "@sentry/react";
import {
  getConsent,
  hasChosen,
  hydrateConsentFromAccount,
  registerAccountConsentSync,
  type AccountConsentState,
  type ConsentState,
} from "./consent";

const API_BASE = `${(import.meta.env.BASE_URL || "/").replace(/\/$/, "")}/api`;

// The ts of the last state we know the account already holds — used to skip
// redundant writes (e.g. re-connect on token refresh, hydration echo).
let lastKnownAccountTs = 0;

function breadcrumb(message: string, data?: Record<string, unknown>): void {
  try {
    Sentry.addBreadcrumb({ category: "consent", level: "info", message, data });
  } catch {
    /* diagnostics must never break consent */
  }
}

async function fetchAccountConsent(token: string): Promise<AccountConsentState | null> {
  const res = await fetch(`${API_BASE}/users/me/consent`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`consent fetch failed (${res.status})`);
  const body = (await res.json()) as { consent: AccountConsentState | null };
  return body.consent ?? null;
}

async function pushAccountConsent(token: string, s: ConsentState): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/users/me/consent`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ analytics: s.analytics, marketing: s.marketing, ts: s.ts }),
    });
    if (res.ok) lastKnownAccountTs = Math.max(lastKnownAccountTs, s.ts);
    return res.ok;
  } catch {
    return false;
  }
}

async function syncOnce(token: string): Promise<void> {
  try {
    const remote = await fetchAccountConsent(token);
    if (remote) lastKnownAccountTs = Math.max(lastKnownAccountTs, remote.ts);
    const local = hasChosen() ? getConsent() : null;
    if (local && (!remote || local.ts > remote.ts)) {
      // Pre-login (or fresher) browser choice → save it to the account.
      const ok = await pushAccountConsent(token, local);
      breadcrumb("consent_synced_to_account", { ok, ts: local.ts });
    } else if (remote && (!local || remote.ts > local.ts)) {
      // Account choice is the freshest (or only) copy → hydrate it so the
      // banner never (re-)shows on this storage-blocked device.
      const applied = hydrateConsentFromAccount(remote);
      if (applied) {
        breadcrumb("consent_hydrated_from_account", {
          analytics: remote.analytics,
          marketing: remote.marketing,
          ts: remote.ts,
        });
      }
    }
  } catch {
    // Best-effort: a failed sync degrades to today's behavior.
  }
}

/**
 * Wire the account consent layer for the authenticated session. Idempotent;
 * safe to call again on token refresh (redundant writes are skipped via the
 * last-known account ts).
 */
export function connectAccountConsent(token: string): void {
  registerAccountConsentSync(async (s) => {
    if (s.ts <= lastKnownAccountTs) return true; // account already has it
    return pushAccountConsent(token, s);
  });
  void syncOnce(token);
}

/** Detach the account layer on logout. */
export function disconnectAccountConsent(): void {
  registerAccountConsentSync(null);
  lastKnownAccountTs = 0;
}
